import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';

// Define types for request and response
export interface UserCreateInput {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  employee_id: string;
  role: string;
  contact_number: string;
  reporting_manager: string;
  department: string;
  user_locations: string;
  language: string;
}

export interface UserInviteInput {
  first_name: string;
  last_name: string;
  email: string;
  employee_id: string;
  role: string;
  phone_number: string;
  // Optional fields — backend (Auth0UserInviteSerializer) treats these as
  // not required. They are omitted from the request when left empty.
  reporting_manager?: string;
  department?: string;
  location?: string;
  // language: string;
  // Present only when the org's user_creation_mode allows direct creation —
  // see company_details.feature_flags.user_creation_mode on the auth0/me response.
  password?: string;
  // Creation-time-only toggle: false means no login profile is created for
  // this user (no IdP account, no invite, no password) — see InviteUserForm's
  // "Enable Application Access" switch. Defaults to true server-side when omitted.
  has_app_access?: boolean;
}

export type UserCreationMode = 'invite' | 'direct';

export interface ResetPasswordResponse {
  email: string;
  organization: string;
  password: string;
}

export interface UserRoles {
  id: string;
  name: string;
  description: string;
  role_type: string;
  is_system_role: boolean;
  company: string;
  company_name: string;
}

export interface UserRolePermission {
  role_id: string;
  role_name: string;
  permission_id: string;
  permission_name: string;
  permission_description: string;
  company_id: string;
}

export interface User {
  [x: string]: any;
  company_details: {
    id: string;
    name: string;
    description: string;
    profile: {
      logo: string | null;
      logo_url: string;
      id: string;
      company: string;
      currency_code: string | null;
      currency: {
        code: string;
        symbol: string;
        name: string;
      } | null;
    }
    feature_flags?: {
      user_creation_mode?: UserCreationMode;
    };
  };
  roles?: UserRoles[];
  role_ids?: string[];
  permissions?: UserRolePermission[];
  additional_info: {
    profile_picture: string | null;
    bio: string;
    date_of_birth: string | null;
    address: string;
  };
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  name: string;
  is_active: boolean;
  auth0_user_id: string | null;
  auth0_invitation_id: string | null;
  invitation_sent_at: string | null;
  invitation_accepted_at: string | null;
  role: string;
  department?: string;
  position?: string;
  employee_id?: string;
  phone_number?: string;
}

export interface UserListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: User[];
}

export interface UserRolesListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: UserRoles[];
}

export interface UserRolePermissionListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: UserRolePermission[];
}

export interface UserFilterParams {
  page?: number;
  limit?: number;
  invitation_status?: 'sent' | 'accepted' | 'all' | 'notinvited';
  dropdown?: boolean;
  name?: string;
  email?: string;
  employee_id?: string;
  role?: string;
  roles?: string[];
  is_active?: boolean;
  departments?: string[];
  locations?: string[];
  phone_number?: string;
  reporting_manager?: string;
  invitation_sent_at_after?: string;
  invitation_sent_at_before?: string;
  invitation_accepted_at_after?: string;
  invitation_accepted_at_before?: string;
  ordering?: string;
  search?: string;
  // Allow any other dynamic filter params
  [key: string]: string | number | boolean | string[] | undefined;
}

export interface TargetUserFilterParams {
  page?: number;
  limit?: number;
  search?: string;
  department_id?: string;
  location_id?: string;
}

export interface Auth0MeResponse {
  user: User & {
    allowed_ui_actions?: string[];
  };
  access_token: string;
  expires_in: number;
  expires_at: number;
  issued_at: number;
  token_type: string;
  company_details?: {
    id: string;
    name: string;
    description: string;
    profile?: {
      logo?: string;
    };
  };
}

// Create API
export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: createExtendedTokenBaseQuery(''),
  tagTypes: ['User', 'UserRoles', 'UserPermissions'],
  keepUnusedDataFor: 120,
  endpoints: (builder) => ({
    listUsers: builder.query<UserListResponse, UserFilterParams>({
      query: (params) => {
        const queryParams: Record<string, string | number> = {};
        
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            if (key === 'page_size') {
              // Convert legacy page_size to limit; skip if limit already set
              if (!params.limit) {
                queryParams['limit'] = value as number;
              }
            } else if (Array.isArray(value)) {
              // Handle array values (like roles, departments, locations)
              queryParams[key] = value.join(',');
            } else {
              queryParams[key] = typeof value === 'boolean' ? value.toString() : value;
            }
          }
        });

        // Ensure a default limit of 10 when neither limit nor page_size is provided
        if (!queryParams['limit']) {
          queryParams['limit'] = 10;
        }

        return {
          url: 'users/',
          method: 'GET',
          params: queryParams,
        };
      },
      providesTags: ['User'],
    }),

    getUserById: builder.query<User, string>({
      query: (userId) => ({
        url: `users/${userId}/`,
        method: 'GET',
      }),
      providesTags: (result, error, userId) => [{ type: 'User', id: userId }],
    }),

    getUserRoleById: builder.query<
      UserRolesListResponse,
      { userId: string; page?: number; limit?: number }
    >({
      query: ({ userId, page, limit }) => ({
        url: `users/${userId}/roles/`,
        method: 'GET',
        params: {
          page: page ?? 1,
          limit: limit ?? 10,
        },
      }),
      providesTags: (result, error, { userId }) => [{ type: 'User', id: userId }],
    }),

    getUserRolePermissionById: builder.query<
      UserRolePermissionListResponse,
      { userId: string; page?: number; limit?: number }
    >({
      query: ({ userId, page, limit }) => ({
        url: `users/${userId}/permissions/`,
        method: 'GET',
        params: {
          page: page ?? 1,
          limit: limit ?? 10,
        },
      }),
      providesTags: (result, error, { userId }) => [{ type: 'User', id: userId }],
    }),

    inviteUser: builder.mutation<User, UserInviteInput>({
      query: (userData) => ({
        url: 'users/auth0/invite/',
        method: 'POST',
        body: userData,
      }),
      invalidatesTags: ['User'],
    }),

    createUser: builder.mutation<User, UserCreateInput>({
      query: (userData) => ({
        url: 'users/auth0/invite/',
        method: 'POST',
        body: userData,
      }),
      invalidatesTags: ['User'],
    }),

    listAllUsers: builder.query<
      UserListResponse,
      { dropdown?: boolean; limit?: number }
    >({
      query: (params = {}) => ({
        url: 'users/',
        method: 'GET',
        params: {
          limit: params.limit ?? 10,
          ...(params.dropdown !== undefined ? { dropdown: params.dropdown } : {}),
        },
      }),
    }),

    // New dropdown-specific endpoint
    getUsersDropdown: builder.query<UserListResponse, UserFilterParams>({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params.page) queryParams.append('page', params.page.toString());
        if (params && 'search' in params && params.search) {
          queryParams.append('search', params.search);
        }
        if (params.limit)
          queryParams.append('limit', params.limit.toString());
        if (params.invitation_status && params.invitation_status !== 'all') {
          queryParams.append('invitation_status', params.invitation_status);
        }
        if (params.name) queryParams.append('name', params.name);
        if (params.email) queryParams.append('email', params.email);
        if (params.employee_id) queryParams.append('employee_id', params.employee_id);
        if (params.phone_number) queryParams.append('phone_number', params.phone_number);
        if (params.roles?.length) queryParams.append('roles', params.roles.join(','));
        if (params.departments?.length) queryParams.append('departments', params.departments.join(','));
        if (params.locations?.length) queryParams.append('locations', params.locations.join(','));
        if (params.reporting_manager) queryParams.append('reporting_manager', params.reporting_manager);
        if (params.invitation_sent_at_after) queryParams.append('invitation_sent_at_after', params.invitation_sent_at_after);
        if (params.invitation_sent_at_before) queryParams.append('invitation_sent_at_before', params.invitation_sent_at_before);
        if (params.invitation_accepted_at_after) queryParams.append('invitation_accepted_at_after', params.invitation_accepted_at_after);
        if (params.invitation_accepted_at_before) queryParams.append('invitation_accepted_at_before', params.invitation_accepted_at_before);

        if (params.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
        if (params.ordering) queryParams.append('ordering', params.ordering);

        const queryString = queryParams.toString();
        return {
          url: `users/dropdown/${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['User'],
    }),

    // "Valid-login-only" dropdown — same params/response shape as
    // getUsersDropdown, but backed by users/dropdown/app-access/, which
    // filters to has_app_access=True server-side (HasAppAccessFilterBackend).
    // Use for pickers that require a user with an actual AMS login: Workflow
    // Approvals "specific user" and Schedule–User Group members. Do NOT use
    // for Asset "Assigned To" / Asset Transfer "Target User", which must
    // keep showing every user regardless of access status.
    getAppAccessUsersDropdown: builder.query<UserListResponse, UserFilterParams>({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params.page) queryParams.append('page', params.page.toString());
        if (params && 'search' in params && params.search) {
          queryParams.append('search', params.search);
        }
        if (params.limit)
          queryParams.append('limit', params.limit.toString());
        if (params.name) queryParams.append('name', params.name);
        if (params.email) queryParams.append('email', params.email);
        if (params.employee_id) queryParams.append('employee_id', params.employee_id);
        if (params.phone_number) queryParams.append('phone_number', params.phone_number);
        if (params.roles?.length) queryParams.append('roles', params.roles.join(','));
        if (params.departments?.length) queryParams.append('departments', params.departments.join(','));
        if (params.locations?.length) queryParams.append('locations', params.locations.join(','));
        if (params.reporting_manager) queryParams.append('reporting_manager', params.reporting_manager);
        if (params.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
        if (params.ordering) queryParams.append('ordering', params.ordering);

        const queryString = queryParams.toString();
        return {
          url: `users/dropdown/app-access/${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['User'],
    }),

    getTargetUsers: builder.query<UserListResponse, TargetUserFilterParams>({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params.page) queryParams.append('page', params.page.toString());
        if (params.limit)
          queryParams.append('limit', params.limit.toString());
        if (params.search) queryParams.append('search', params.search);
        if (params.department_id) queryParams.append('department_id', params.department_id);
        if (params.location_id) queryParams.append('location_id', params.location_id);

        const queryString = queryParams.toString();
        return {
          url: `assets/transfers/requests/target-custodians/${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['User'],
    }),

    getCurrentUser: builder.query<User, void>({
      query: () => ({
        url: 'users/me/',
        method: 'GET',
      }),
      providesTags: [{ type: 'User', id: 'me' }],
      keepUnusedDataFor: 300,
    }),

    // New endpoint for auth0/me
    getAuth0Me: builder.query<Auth0MeResponse, void>({
      query: () => ({
        url: 'users/auth0/me/',
        method: 'GET',
      }),
      providesTags: ['User'],
    }),

    resendInvitation: builder.mutation<User, string>({
      query: (userId) => ({
        url: `users/${userId}/resend-invitation/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, userId) => [
        { type: 'User', id: userId },
      ],
    }),

    resetPassword: builder.mutation<ResetPasswordResponse, string>({
      query: (userId) => ({
        url: `users/${userId}/reset-password/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, userId) => [
        { type: 'User', id: userId },
      ],
    }),
    updateUser: builder.mutation<
      User,
      { userId: string; userData: Partial<User> }
    >({
      query: ({ userId, userData }) => ({
        url: `users/${userId}/`,
        method: 'PATCH',
        body: userData,
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: 'User', id: userId },
      ],
    }),
  }),
});

// Export hooks
export const {
  useListUsersQuery,
  useGetUserByIdQuery,
  useGetUserRoleByIdQuery,
  useGetUserRolePermissionByIdQuery,
  useInviteUserMutation,
  useCreateUserMutation,
  useResendInvitationMutation,
  useResetPasswordMutation,
  useListAllUsersQuery,
  useUpdateUserMutation,
  useGetCurrentUserQuery,
  useGetAuth0MeQuery,
  useGetUsersDropdownQuery,
  useGetAppAccessUsersDropdownQuery,
  useGetTargetUsersQuery,
} = userApi;
